import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, writeBatch, query, where } from 'firebase/firestore';
import { ArrowLeft, Search, Merge, CheckCircle, AlertTriangle, Loader2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Registration, Firm } from '../types';

interface FirmGroup {
  canonical: string;
  variants: string[];
  count: number;
  registrations: Registration[];
}

interface MergeOperation {
  from: string;
  to: string;
  count: number;
}

export default function Migration() {
  const { signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [firmGroups, setFirmGroups] = useState<FirmGroup[]>([]);
  const [selectedMerges, setSelectedMerges] = useState<Map<string, string>>(new Map());
  const [mergeProgress, setMergeProgress] = useState<{ current: number; total: number } | null>(null);
  const [mergeResults, setMergeResults] = useState<MergeOperation[]>([]);
  const [showResults, setShowResults] = useState(false);

  const normalizeFirmName = (name: string): string => {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  const findSimilarFirms = (firms: string[]): FirmGroup[] => {
    const groups = new Map<string, string[]>();
    
    // Group firms by normalized name
    firms.forEach(firm => {
      const normalized = normalizeFirmName(firm);
      if (!groups.has(normalized)) {
        groups.set(normalized, []);
      }
      groups.get(normalized)!.push(firm);
    });

    // Only return groups with multiple variants
    return Array.from(groups.entries())
      .filter(([, variants]) => variants.length > 1)
      .map(([normalized, variants]) => {
        // Choose the most common variant as canonical, or the one with proper capitalization
        const canonical = variants.reduce((best, current) => {
          // Prefer variants with proper capitalization (first letter uppercase)
          if (current[0] === current[0].toUpperCase() && best[0] !== best[0].toUpperCase()) {
            return current;
          }
          // If both or neither have proper capitalization, prefer the shorter one (less likely to have extra spaces)
          if (current.length < best.length) {
            return current;
          }
          return best;
        });

        return {
          canonical,
          variants: variants.filter(v => v !== canonical),
          count: 0, // Will be filled when we analyze registrations
          registrations: []
        };
      });
  };

  const analyzeData = async () => {
    setAnalyzing(true);
    try {
      // Fetch all registrations
      const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
      const registrations = registrationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Registration & { id: string })[];

      // Get unique firm names
      const firmNames = Array.from(new Set(registrations.map(reg => reg.busCompany)));
      
      // Find similar firms
      const groups = findSimilarFirms(firmNames);
      
      // Count registrations for each group
      const groupsWithCounts = groups.map(group => {
        const allVariants = [group.canonical, ...group.variants];
        const groupRegistrations = registrations.filter(reg => 
          allVariants.includes(reg.busCompany)
        );
        
        return {
          ...group,
          count: groupRegistrations.length,
          registrations: groupRegistrations
        };
      });

      // Sort by count (most registrations first)
      groupsWithCounts.sort((a, b) => b.count - a.count);
      
      setFirmGroups(groupsWithCounts);
      
      // Pre-select canonical names for merging
      const initialMerges = new Map<string, string>();
      groupsWithCounts.forEach(group => {
        group.variants.forEach(variant => {
          initialMerges.set(variant, group.canonical);
        });
      });
      setSelectedMerges(initialMerges);
      
    } catch (error) {
      console.error('Error analyzing data:', error);
      alert('Feil ved analyse av data');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCanonicalChange = (groupIndex: number, newCanonical: string) => {
    const updatedGroups = [...firmGroups];
    const group = updatedGroups[groupIndex];
    
    // Remove old canonical from variants and add to variants
    if (group.canonical !== newCanonical) {
      group.variants.push(group.canonical);
      group.variants = group.variants.filter(v => v !== newCanonical);
      group.canonical = newCanonical;
    }
    
    setFirmGroups(updatedGroups);
    
    // Update selected merges
    const newMerges = new Map(selectedMerges);
    group.variants.forEach(variant => {
      newMerges.set(variant, newCanonical);
    });
    setSelectedMerges(newMerges);
  };

  const executeMerge = async () => {
    await executeMergeForGroups(Array.from(selectedMerges.entries()));
  };

  const executeMergeForGroup = async (groupIndex: number) => {
    const group = firmGroups[groupIndex];
    const groupMerges = group.variants.map(variant => [variant, group.canonical] as [string, string]);
    
    if (groupMerges.length === 0) {
      alert('Ingen sammenslåinger i denne gruppen');
      return;
    }

    if (!confirm(`Er du sikker på at du vil slå sammen ${groupMerges.length} firmanavn i denne gruppen? Dette kan ikke angres.`)) {
      return;
    }

    await executeMergeForGroups(groupMerges);
    
    // Remove the processed group from the list
    const updatedGroups = firmGroups.filter((_, index) => index !== groupIndex);
    setFirmGroups(updatedGroups);
    
    // Update selected merges to remove processed items
    const newMerges = new Map(selectedMerges);
    group.variants.forEach(variant => {
      newMerges.delete(variant);
    });
    setSelectedMerges(newMerges);
  };

  const executeMergeForGroups = async (mergeEntries: [string, string][]) => {
    if (selectedMerges.size === 0) {
      alert('Ingen sammenslåinger valgt');
      return;
    }

    setLoading(true);
    setMergeProgress({ current: 0, total: mergeEntries.length });
    const results: MergeOperation[] = [];

    try {
      let processed = 0;
      
      // Process merges in batches
      const batchSize = 10;
      
      for (let i = 0; i < mergeEntries.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchEntries = mergeEntries.slice(i, i + batchSize);
        
        for (const [fromName, toName] of batchEntries) {
          // Find all registrations with the old firm name
          const registrationsSnapshot = await getDocs(
            query(collection(db, 'registrations'), where('busCompany', '==', fromName))
          );
          
          const count = registrationsSnapshot.docs.length;
          if (count > 0) {
            // Update all registrations
            registrationsSnapshot.docs.forEach(docSnapshot => {
              batch.update(docSnapshot.ref, { busCompany: toName });
            });
            
            results.push({ from: fromName, to: toName, count });
          }
          
          processed++;
          setMergeProgress({ current: processed, total: mergeEntries.length });
        }
        
        // Commit the batch
        await batch.commit();
      }

      // Clean up duplicate firms in the firms collection
      const firmsSnapshot = await getDocs(collection(db, 'firms'));
      const firmsToDelete = firmsSnapshot.docs.filter(doc => 
        selectedMerges.has(doc.data().name)
      );
      
      // Delete old firm entries
      for (const firmDoc of firmsToDelete) {
        await deleteDoc(firmDoc.ref);
      }

      setMergeResults(results);
      
      // Only show results if this was a full merge (not individual group)
      if (mergeEntries.length === selectedMerges.size) {
        setShowResults(true);
        setFirmGroups([]);
        setSelectedMerges(new Map());
      }
      
    } catch (error) {
      console.error('Error executing merge:', error);
      alert('Feil ved sammenslåing av data');
    } finally {
      setLoading(false);
      setMergeProgress(null);
    }
  };

  const downloadReport = () => {
    const csvContent = [
      'Fra,Til,Antall registreringer',
      ...mergeResults.map(result => `${result.from},${result.to},${result.count}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `firma_sammenslåing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetAnalysis = () => {
    setFirmGroups([]);
    setSelectedMerges(new Map());
    setMergeResults([]);
    setShowResults(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Migrering og dataopprydding</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {showResults ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Sammenslåing fullført</h2>
                    <p className="text-gray-600">
                      {mergeResults.length} firmanavn ble slått sammen, 
                      totalt {mergeResults.reduce((sum, r) => sum + r.count, 0)} registreringer oppdatert
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadReport}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Last ned rapport
                  </button>
                  <button
                    onClick={resetAnalysis}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Ny analyse
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Til
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringer oppdatert
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mergeResults.map((result, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {result.from}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {result.to}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Firmanavn opprydding</h2>
                <p className="text-gray-600 mb-6">
                  Dette verktøyet finner og slår sammen lignende firmanavn som "Ollex", "ollex", " Ollex " osv.
                  Alle registreringer vil bli oppdatert til å bruke det kanoniske navnet.
                </p>
                
                <div className="flex space-x-4">
                  <button
                    onClick={analyzeData}
                    disabled={analyzing || loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyserer...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Analyser data
                      </>
                    )}
                  </button>
                  
                  {firmGroups.length > 0 && (
                    <button
                      onClick={executeMerge}
                      disabled={loading || selectedMerges.size === 0}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Slår sammen...
                        </>
                      ) : (
                        <>
                          <Merge className="h-4 w-4 mr-2" />
                          Slå sammen ({selectedMerges.size})
                        </>
                      )}
                    </button>
                  )}
                </div>

                {mergeProgress && (
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Behandler sammenslåinger...</span>
                      <span>{mergeProgress.current} / {mergeProgress.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(mergeProgress.current / mergeProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {firmGroups.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <AlertTriangle className="h-6 w-6 text-yellow-500 mr-2" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          Funnet {firmGroups.length} grupper med lignende navn
                        </h3>
                        <p className="text-sm text-gray-600">
                          Velg det kanoniske navnet for hver gruppe. Alle varianter vil bli slått sammen til dette navnet.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {firmGroups.map((group, groupIndex) => (
                      <div key={groupIndex} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              Gruppe {groupIndex + 1}
                            </span>
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {group.count} registreringer
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => executeMergeForGroup(groupIndex)}
                            disabled={loading}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
                          >
                            {loading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Behandler...
                              </>
                            ) : (
                              <>
                                <Merge className="h-4 w-4 mr-2" />
                                Slå sammen denne gruppen
                              </>
                            )}
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Kanonisk navn (alle vil bli endret til dette):
                            </label>
                            <select
                              value={group.canonical}
                              onChange={(e) => handleCanonicalChange(groupIndex, e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value={group.canonical}>{group.canonical}</option>
                              {group.variants.map(variant => (
                                <option key={variant} value={variant}>{variant}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Varianter som vil bli slått sammen:
                            </label>
                            <div className="space-y-1">
                              {group.variants.map(variant => (
                                <div key={variant} className="flex items-center text-sm">
                                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                    "{variant}"
                                  </span>
                                  <span className="ml-2 text-gray-500">
                                    → "{group.canonical}"
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}